import React, { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { formatCurrency } from '../charts/d3/format';
import { GlassCard } from '../ui/GlassCard';
import { colors, spacing, typography } from '../../core/theme/tokens';
import type { SpendCategoryNode } from '../../types/financial';

type SpendCategoryAccordionProps = {
  categories: SpendCategoryNode[];
  onMerchantPress: (categoryId: string, merchantId: string) => void;
  /** Open category transaction list (preferred over expand when provided). */
  onCategoryPress?: (categoryId: string) => void;
};

export function SpendCategoryAccordion({
  categories,
  onMerchantPress,
  onCategoryPress,
}: SpendCategoryAccordionProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const toggle = (categoryId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  return (
    <GlassCard>
      <Text style={styles.sectionTitle}>Categories</Text>
      <View style={styles.list}>
        {categories.map((category) => {
          const hasMerchants = category.merchants.length > 0;
          const categoryNav = !!onCategoryPress;
          const expanded = expandedIds.has(category.id);
          const txCount = category.transactionCount;
          const merchantCount = category.merchants.length;
          const hint = categoryNav
            ? txCount != null
              ? `${txCount} transaction${txCount === 1 ? '' : 's'}`
              : hasMerchants
                ? `${merchantCount} merchant${merchantCount === 1 ? '' : 's'} · view all`
                : 'View transactions'
            : hasMerchants
              ? `${merchantCount} merchant${merchantCount === 1 ? '' : 's'}`
              : txCount != null
                ? `${txCount} transaction${txCount === 1 ? '' : 's'}`
                : 'View transactions';

          return (
            <View key={category.id} style={styles.group}>
              <Pressable
                accessibilityRole="button"
                accessibilityState={categoryNav ? undefined : { expanded }}
                accessibilityLabel={
                  categoryNav
                    ? `${category.name}, ${formatCurrency(category.amount)}. View transactions`
                    : `${category.name}, ${formatCurrency(category.amount)}. ${
                        expanded ? 'Collapse' : 'Expand'
                      }`
                }
                onPress={() => {
                  if (categoryNav) {
                    onCategoryPress(category.id);
                    return;
                  }
                  toggle(category.id);
                }}
                style={({ pressed }) => [styles.categoryRow, pressed && styles.rowPressed]}
              >
                <View style={[styles.swatch, { backgroundColor: category.color }]} />
                <View style={styles.meta}>
                  <Text style={styles.rowLabel}>{category.name}</Text>
                  <Text style={styles.rowHint}>{hint}</Text>
                </View>
                <Text style={styles.rowValue}>{formatCurrency(category.amount)}</Text>
                <Text
                  style={[
                    styles.chevron,
                    !categoryNav && expanded && styles.chevronOpen,
                    categoryNav && styles.chevronLive,
                  ]}
                >
                  ›
                </Text>
              </Pressable>

              {!categoryNav && expanded && (
                <View style={styles.merchantList}>
                  {category.merchants.map((merchant) => (
                    <Pressable
                      key={merchant.id}
                      accessibilityRole="button"
                      accessibilityLabel={`${merchant.name}, ${formatCurrency(merchant.amount)}`}
                      onPress={() => onMerchantPress(category.id, merchant.id)}
                      style={({ pressed }) => [
                        styles.merchantRow,
                        pressed && styles.rowPressed,
                      ]}
                    >
                      <View style={styles.meta}>
                        <Text style={styles.merchantLabel}>{merchant.name}</Text>
                        <Text style={styles.rowHint}>
                          {merchant.transactions.length} transaction
                          {merchant.transactions.length === 1 ? '' : 's'}
                        </Text>
                      </View>
                      <Text style={styles.rowValue}>{formatCurrency(merchant.amount)}</Text>
                    </Pressable>
                  ))}
                </View>
              )}
            </View>
          );
        })}
      </View>
    </GlassCard>
  );
}

const styles = StyleSheet.create({
  sectionTitle: {
    ...typography.headline,
    color: colors.text,
    marginBottom: spacing.md,
  },
  list: {
    gap: spacing.xs,
  },
  group: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  categoryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minHeight: 44,
    paddingVertical: spacing.sm,
  },
  merchantList: {
    paddingLeft: spacing.lg,
    paddingBottom: spacing.sm,
    gap: 2,
  },
  merchantRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    minHeight: 44,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
  },
  rowPressed: {
    opacity: 0.88,
  },
  swatch: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  meta: {
    flex: 1,
    gap: 2,
  },
  rowLabel: {
    ...typography.body,
    color: colors.text,
  },
  merchantLabel: {
    ...typography.body,
    color: colors.text,
    fontSize: 15,
  },
  rowHint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  rowValue: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
  },
  chevron: {
    ...typography.body,
    color: colors.textMuted,
    fontSize: 22,
    lineHeight: 24,
    transform: [{ rotate: '0deg' }],
    width: 18,
    textAlign: 'center',
  },
  chevronOpen: {
    transform: [{ rotate: '90deg' }],
    color: colors.accent,
  },
  chevronLive: {
    color: colors.accent,
  },
});
